import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { usePreferences } from '../hooks/usePreferences';
import { getCurrentLanguage } from '../i18n/i18n';
import './Settings.css';

const Settings = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');
  const [notification, setNotification] = useState(null);
  
  // Hook to manage preferences
  const {
    preferences,
    loading,
    saving,
    error,
    savePreferences,
    saveLanguage,
    saveTheme,
    saveNotifications,
    resetPreferences,
    clearError
  } = usePreferences();
  
  // Local state for current modifications
  const [localPreferences, setLocalPreferences] = useState({
    language: 'fr',
    theme: 'light',
    notifications: {
      email: true,
      push: false,
      system: true
    },
    debugMode: false
  });

  // Synchronize with loaded preferences
  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  // Handle notifications
  useEffect(() => {
    if (error) {
      setNotification({ type: 'error', message: error });
      clearError();
    }
  }, [error, clearError]);

  const tabs = [
    { id: 'general', label: t('settings.general'), icon: '⚙️' },
    { id: 'appearance', label: t('settings.appearance'), icon: '🎨' },
    { id: 'privacy', label: t('settings.privacy'), icon: '🔒' },
    { id: 'advanced', label: t('settings.advanced'), icon: '🔧' }
  ];

  const handleSave = async () => {
    try {
      // Synchroniser la langue courante avec localPreferences avant sauvegarde
      const currentLanguage = getCurrentLanguage();
      const preferencesToSave = {
        ...localPreferences,
        language: currentLanguage // Force la langue actuelle du i18n
      };
      
      console.log(`💾 Sauvegarde settings avec langue: ${currentLanguage}`);
      
      const result = await savePreferences(preferencesToSave);
      
      if (result.success) {
        setNotification({ 
          type: 'success', 
          message: result.message || t('settings.settingsSaved') 
        });
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setNotification({ 
          type: 'error', 
          message: result.error || t('settings.settingsError') 
        });
      }
    } catch (error) {
      setNotification({ 
        type: 'error', 
        message: t('settings.settingsError') 
      });
    }
  };

  const handleReset = async () => {
    if (window.confirm(t('settings.resetSettings') + ' ?')) {
      try {
        const result = await resetPreferences();
        
        if (result.success) {
          setNotification({ 
            type: 'success', 
            message: result.message || t('settings.resetSuccess')
          });
          
          // Local preferences will be updated automatically via the effect
        } else {
          setNotification({ 
            type: 'error', 
            message: result.error || t('settings.settingsError') 
          });
        }
      } catch (error) {
        setNotification({ 
          type: 'error', 
          message: t('settings.settingsError') 
        });
      }
    }
  };

  const handleThemeChange = (theme) => {
    setLocalPreferences(prev => ({
      ...prev,
      theme
    }));
  };

  const handleNotificationChange = (type, value) => {
    setLocalPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: value
      }
    }));
  };

  const handleDebugModeChange = (value) => {
    setLocalPreferences(prev => ({
      ...prev,
      debugMode: value
    }));
  };

  // Close notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <div className="settings-overlay">
      <div className="settings-container">
        {/* Notification */}
        {notification && (
          <div className={`settings-notification ${notification.type}`}>
            <span className="notification-icon">
              {notification.type === 'success' ? '✅' : '❌'}
            </span>
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
            >
              ✕
            </button>
          </div>
        )}

        {/* Header */}
        <div className="settings-header">
          <h2 className="settings-title">
            <span className="settings-icon">⚙️</span>
            {t('settings.title')}
            {loading && <span className="loading-spinner-small">⏳</span>}
          </h2>
          <button 
            className="settings-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        <div className="settings-content">
          {/* Navigation tabs */}
          <div className="settings-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content panels */}
          <div className="settings-panel">
            {activeTab === 'general' && (
              <div className="settings-section">
                <h3 className="section-title">{t('settings.general')}</h3>
                
                {/* Language selector */}
                <div className="setting-item">
                  <LanguageSelector />
                </div>

                {/* User info */}
                <div className="setting-item">
                  <label className="setting-label">
                    {t('profile.personalInfo')}
                  </label>
                  <div className="user-info-card">
                    <div className="user-avatar">
                      {user?.firstName?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="user-details">
                      <div className="user-name">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div className="user-email">{user?.email}</div>
                      <div className="user-role">{user?.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="settings-section">
                <h3 className="section-title">{t('settings.appearance')}</h3>
                
                <div className="setting-item">
                  <label className="setting-label">
                    {t('settings.theme')}
                  </label>
                  <div className="theme-selector">
                    <button 
                      className={`theme-option ${localPreferences.theme === 'light' ? 'active' : ''}`}
                      onClick={() => handleThemeChange('light')}
                    >
                      <span className="theme-preview theme-light"></span>
                      <span className="theme-name">{t('settings.lightTheme')}</span>
                    </button>
                    <button 
                      className={`theme-option ${localPreferences.theme === 'dark' ? 'active' : ''}`}
                      onClick={() => handleThemeChange('dark')}
                      disabled
                      style={{ opacity: 0.6, cursor: 'not-allowed', position: 'relative' }}
                    >
                      <span className="theme-preview theme-dark"></span>
                      <span className="theme-name">
                        {t('settings.darkTheme')}
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '11px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          verticalAlign: 'middle'
                        }}>
                          🚧 WIP
                        </span>
                      </span>
                    </button>
                    <button 
                      className={`theme-option ${localPreferences.theme === 'auto' ? 'active' : ''}`}
                      onClick={() => handleThemeChange('auto')}
                    >
                      <span className="theme-preview theme-auto"></span>
                      <span className="theme-name">{t('settings.autoTheme')}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="settings-section">
                <h3 className="section-title">{t('settings.privacy')}</h3>
                
                <div className="setting-item">
                  <label className="setting-label">
                    {t('notifications.settings')}
                  </label>
                  <div className="toggle-group">
                    <div className="toggle-item">
                      <span>{t('notifications.emailNotifications')}</span>
                      <div className="toggle-switch">
                        <input 
                          type="checkbox" 
                          id="email-notifications" 
                          checked={localPreferences.notifications?.email || false}
                          onChange={(e) => handleNotificationChange('email', e.target.checked)}
                        />
                        <label htmlFor="email-notifications"></label>
                      </div>
                    </div>
                    <div className="toggle-item">
                      <span>{t('notifications.pushNotifications')}</span>
                      <div className="toggle-switch">
                        <input 
                          type="checkbox" 
                          id="push-notifications" 
                          checked={localPreferences.notifications?.push || false}
                          onChange={(e) => handleNotificationChange('push', e.target.checked)}
                        />
                        <label htmlFor="push-notifications"></label>
                      </div>
                    </div>
                    <div className="toggle-item">
                      <span>{t('notifications.systemNotifications')}</span>
                      <div className="toggle-switch">
                        <input 
                          type="checkbox" 
                          id="system-notifications" 
                          checked={localPreferences.notifications?.system || false}
                          onChange={(e) => handleNotificationChange('system', e.target.checked)}
                        />
                        <label htmlFor="system-notifications"></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="settings-section">
                <h3 className="section-title">{t('settings.advanced')}</h3>
                
                {/* Debug Mode - SUPPRIMÉ (logs toujours actifs en dev)
                <div className="setting-item">
                  <label className="setting-label">
                    Debug Mode
                  </label>
                  <div className="toggle-switch">
                    <input 
                      type="checkbox" 
                      id="debug-mode" 
                      checked={localPreferences.debugMode || false}
                      onChange={(e) => handleDebugModeChange(e.target.checked)}
                    />
                    <label htmlFor="debug-mode"></label>
                  </div>
                  <div className="setting-help">
                    Activer les logs de débogage pour le développement
                  </div>
                </div>
                */}

                <div className="setting-item">
                  <label className="setting-label">
                    Version de l'application
                  </label>
                  <div className="version-info">
                    <span className="version-number">v1.0.0</span>
                    <span className="version-build">Build 2024.1</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="settings-footer">
          <button 
            className="btn-secondary"
            onClick={handleReset}
            disabled={saving}
          >
            {t('settings.resetSettings')}
          </button>
          <button 
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '⏳ ' + t('common.processing') : t('settings.saveSettings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
